import { watch } from "vue";

import { PosClient } from "@smeetz/pos-client";
import { setTranslator } from "@smeetz/i18n";

import i18n from "@/lang";
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";
import { LogService } from "@/services/logging";

let posClient: PosClient | null = null;
let initPromise: Promise<void> | null = null;
let groupSyncInstalled = false;

/**
 * Resolve the modelkit env literal from `VITE_ENV`. Defaults to `"prod"`
 * for any unrecognised value so the modelkit never silently points at a wrong
 * cluster.
 *
 * @returns {"dev" | "test" | "prod"} Modelkit env identifier.
 */
function resolveEnv(): "dev" | "test" | "prod" {
  const raw = import.meta.env.VITE_ENV;

  return raw === "dev" || raw === "test" ? raw : "prod";
}

/**
 * Initialize `@smeetz/pos-client` once per session: loads CDN i18n dictionary
 * for the host locale, bridges `@smeetz/i18n.t()` to pos's vue-i18n instance
 * (so DS components like SSearchTable / SDynamicFilter resolve their internal
 * keys), and initializes the Elasticsearch modelkit using the authenticated
 * user's `elasticSearchTokens` (so SSearchTable's BookingCollection can hit
 * the cluster).
 *
 * Must be called after `UserModule.fetchUser()` completes — tokens come from
 * the user response. Subsequent calls return the same promise (idempotent).
 *
 * @returns {Promise<void>} Resolves once i18n dictionaries are loaded and
 *   modelkit is wired.
 */
export function ensurePosClientInit(): Promise<void> {
  if (initPromise) {
    return initPromise;
  }

  const tokens = UserModule.getUser?.elasticSearchTokens;

  // Don't cache a token-less client: a navigation can reach here before
  // fetchUser has loaded the tokens, and the cache would serve it until reload.
  if (!tokens || Object.keys(tokens).length === 0) {
    return Promise.resolve();
  }

  const groupAccess = POSConfigModule.groupId ?? UserModule.getUserGroupId ?? 0;
  const locale = i18n.global.locale.value as string;

  const client = new PosClient({
    locale,
    env: resolveEnv(),
    groupAccess,
    elasticSearchTokens: tokens
      ? (tokens as unknown as Record<string, string>)
      : undefined,
  });

  posClient = client;

  initPromise = (async () => {
    try {
      await client.init();

      // Merge CDN dictionary on top of pos's existing lang JSON messages so
      // DS internal keys resolve while pos's own keys keep working.
      const messages = client.i18n?.current ?? {};

      i18n.global.mergeLocaleMessage(locale, messages);

      // Bridge @smeetz/i18n's `t()` to pos's vue-i18n. DS components import
      // `t` directly from @smeetz/i18n; without this they'd return the raw
      // key for every translation lookup.
      //
      // Cast through `any` to avoid TS2589 (excessively deep type instantiation)
      // caused by vue-i18n v11's complex overloaded generics on `t()`. Same
      // workaround used in src/utils/vue-helpers/orders.ts:42.
      const translate: (key: string) => string = (i18n.global as any).t;

      setTranslator(translate);
    } catch (err) {
      LogService.info("[pos-client-init] init failed", { err });
    }
  })();

  return initPromise;
}

/**
 * Force the modelkit to reinitialize for a new group access. Use after a
 * group switch when the same authenticated user changes which group's data
 * they want to query.
 *
 * @param {number | string} next - New group access id.
 * @returns {boolean} Sentinel.
 */
export function setPosClientGroupAccess(next: number | string): boolean {
  if (!posClient) {
    return true;
  }

  posClient.setGroupAccess(next);

  return true;
}

/**
 * Install a single reactive watcher on `POSConfigModule.groupId` that pushes
 * the new value into the pos-client whenever it changes. Solves the
 * fresh-login race where `ensurePosClientInit()` fires before the user picks
 * a POS config — at that point `groupId` is null and modelkit gets stuck on
 * `groupAccess: 0`. The watcher catches the subsequent POS-config selection
 * and reinitialises modelkit with the real group.
 *
 * Memory profile: one Vue 3 reactive watcher (one closure + one dep entry).
 * Zero work when groupId does not change. Lives for the entire session — not
 * stopped because there is nothing to scope it to (POS terminals stay open).
 * Guarded with `groupSyncInstalled` so a second call is a no-op (defensive
 * against HMR module re-evaluation duplicating watchers).
 *
 * Call once from `main.ts` after the store is registered (alongside
 * `setupPermissionGuard()`).
 *
 * @returns {boolean} Sentinel.
 */
export function setupPosClientGroupSync(): boolean {
  if (groupSyncInstalled) {
    return true;
  }

  groupSyncInstalled = true;

  watch(
    () => POSConfigModule.groupId,
    (next) => {
      if (!next) {
        return undefined;
      }

      setPosClientGroupAccess(next);

      return true;
    }
  );

  return true;
}
